import { TypedDocumentNode, useQuery, WatchQueryFetchPolicy } from "@apollo/client";
import { Box, SxProps, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { GridSortModel } from "@mui/x-data-grid-premium";
import {
  forwardRef,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { FieldError } from "react-hook-form";
import { useDebounce } from "use-debounce";

import { ErrorDisplay } from "./ErrorDisplay";

type OmnisearchPaginationModel = {
  search: string;
  page: number;
  pageSize: number;
  sort: GridSortModel;
};

/**
 * Props for the OmniAutocompleteProps component.
 */
interface AsyncAutocompleteProps<
  TData,
  TOption extends { id: string | number },
  TAdditionalVars extends Record<string, unknown>,
  TMultiple extends boolean | undefined = false,
> {
  /** GraphQL query document */
  query: TypedDocumentNode<TData, TAdditionalVars>;
  /** Function to extract items from the query result */
  getItems: (data: TData) => TOption[];
  /** Function to extract the label from an option */
  getOptionLabel: (option: TOption) => string;
  placeholderLabel?: string;
  /** Names the input with an aria-label instead of a floating label, so a visible heading isn't repeated. */
  hideLabel?: boolean;
  /** Additional variables for the query */
  variables?: Partial<TAdditionalVars>;
  /** Initial pagination model */
  initialPaginationModel?: Partial<OmnisearchPaginationModel>;
  /** Initial page size for pagination (minimum 25; values below are clamped to
   * ensure that the list is scrollable to trigger loading more items ) */
  initialPageSize?: number;
  /** Initial sort model override */
  initialSortModel?: GridSortModel;
  /** Callback for when an option is selected */
  onChange?: (value: TMultiple extends true ? TOption[] : TOption | null) => void;
  /** The value of the autocomplete */
  value?: TMultiple extends true ? TOption[] : TOption | null;
  /** Error to display */
  error?: FieldError;
  /** Display in a disabled state */
  disabled?: boolean;
  /** Prevent clearing the selection */
  disableClearable?: boolean;
  /** Additional search string to append to the search query */
  additionalSearchString?: string;
  helperText?: string | React.JSX.Element;
  additionalEndAdornment?: React.ReactNode;
  /** Enable multiple selection mode */
  multiple?: TMultiple;
  /** Filter selected options from dropdown */
  filterSelectedOptions?: boolean;
  /** Function to disable specific options */
  getOptionDisabled?: (option: TOption) => boolean;
  /** Custom option renderer */
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: TOption) => React.ReactNode;
  sx?: SxProps;
  loading?: boolean;
  /** Optional custom equality comparator */
  isOptionEqualToValue?: (option: TOption, value: TOption) => boolean;
  /** Optional filterOptions override for custom option creation */
  filterOptions?: (options: TOption[], params: any) => TOption[];
  /** Pass through ListboxProps (merged with internal onScroll) */
  listboxProps?: React.HTMLAttributes<HTMLUListElement>;
  /** Delay querying until the dropdown is opened */
  lazyLoad?: boolean;
  /** onBlur passthrough */
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  /** Optional TextField props passthrough */
  textFieldProps?: TextFieldProps;
  /** Callback when options list changes (for external logic) */
  onOptionsUpdated?: (options: TOption[]) => void;
  /** Apollo fetch policy for the query (default: cache-and-network for freshness) */
  fetchPolicy?: WatchQueryFetchPolicy;
  /** Key getter for option identity used for de-duplication; defaults to (o: any) => o.id */
  getOptionKey?: (option: TOption) => string | number;
  /** Custom no options text override */
  noOptionsText?: React.ReactNode;
  /** Hide error display (useful when using grouped error display) */
  hideErrors?: boolean;
  /** Callback when dropdown opens */
  onOpen?: () => void;
  /** Callback when dropdown closes */
  onClose?: () => void;
  /** When true, the autocomplete root spans the container width (matches `FormControl` + `Select`). */
  fullWidth?: boolean;
}

/**
 * AsyncAutocomplete Component
 * A generic autocomplete component that supports pagination, GraphQL integration,
 * and both single and multiple selection modes.
 *
 * @param query - The GraphQL query document to fetch data.
 * @param getItems - Function to extract items from the query result.
 * @param getOptionLabel - Function to extract the label from an option.
 * @param variables - Variables for the query.
 * @param onChange - Callback for when an option is selected.
 * @param value - The value of the autocomplete (single item or array for multiple mode).
 * @param error - Error to display.
 * @param multiple - Enable multiple selection mode.
 * @param filterSelectedOptions - Filter selected options from dropdown (multiple mode only).
 * @param getOptionDisabled - Function to disable specific options.
 * @param renderOption - Custom option renderer.
 * @returns A JSX element representing the paginated autocomplete component.
 *
 * @example Single Selection
 * ```tsx
 * <AsyncAutocomplete
 *   query={FIRE_DEPARTMENTS_QUERY}
 *   variables={{
 *     state: facilityState,
 *     county: facilityCounty,
 *   }}
 *   getItems={(data: FireDepartmentsQuery) =>
 *     data.fireDepartments?.items ?? []
 *   }
 *   onChange={onChange}
 *   getOptionLabel={(fireDepartment) =>
 *     `${fireDepartment.name} ${fireDepartment.city}, ${fireDepartment.state}`
 *   }
 *   value={value}
 *   error={error}
 * />
 * ```
 *
 * @example Multiple Selection
 * ```tsx
 * <AsyncAutocomplete
 *   query={FACILITIES_QUERY}
 *   getItems={(data) => data.facilities?.items ?? []}
 *   getOptionLabel={(facility) => facility.name ?? ""}
 *   onChange={(facilities) => setSelectedFacilities(facilities as Facility[])}
 *   value={selectedFacilities}
 *   multiple
 *   filterSelectedOptions
 *   placeholderLabel="Select Facilities"
 * />
 * ```
 */
function AsyncAutocompleteComponent<
  TData,
  TOption extends { id: string | number },
  TAdditionalVars extends Record<string, unknown>,
  TMultiple extends boolean | undefined = false,
>(
  {
    query,
    getItems,
    getOptionLabel,
    placeholderLabel = "Search",
    hideLabel = false,
    variables = {},
    initialPageSize = 20,
    initialSortModel,
    onChange,
    value,
    error,
    disabled,
    disableClearable,
    additionalSearchString = "",
    helperText,
    additionalEndAdornment,
    multiple = false,
    filterSelectedOptions = false,
    getOptionDisabled,
    renderOption,
    sx,
    loading = false,
    isOptionEqualToValue,
    filterOptions,
    listboxProps,
    lazyLoad = false,
    onBlur,
    textFieldProps,
    onOptionsUpdated,
    fetchPolicy = "cache-and-network",
    getOptionKey,
    noOptionsText = "No options found.",
    hideErrors = false,
    onOpen: onOpenProp,
    onClose: onCloseProp,
    fullWidth,
  }: AsyncAutocompleteProps<TData, TOption, TAdditionalVars, TMultiple>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  type VariablesType = OmnisearchPaginationModel & Partial<TAdditionalVars>;

  // Local, non-persistent state for search, paging, sort
  const [localSearch, setLocalSearch] = useState<string>("");
  // Keep a ref in sync with page to avoid stale-closure reads during rapid scroll
  const pageRef = useRef<number>(0);
  const minPageSize = 25;
  const effectiveInitialPageSize = Math.max(initialPageSize ?? minPageSize, minPageSize);
  const [localPageSize, setLocalPageSize] = useState<number>(effectiveInitialPageSize);
  const sortModel: GridSortModel = useMemo(
    () => initialSortModel ?? [{ field: "name", sort: "asc" }],
    [initialSortModel]
  );
  const pageSize = localPageSize;
  const [options, setOptions] = useState<TOption[]>([]);
  const [hasMoreOptions, setHasMoreOptions] = useState<boolean>(true);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [awaitingFirstPage, setAwaitingFirstPage] = useState<boolean>(false);
  // Prevent overlapping fetchMore calls which can be triggered by rapid scroll events
  const isFetchingMoreRef = useRef<boolean>(false);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);

  // Stabilize external variables by content to avoid unnecessary resets/refetches
  const variablesKey = useMemo(() => JSON.stringify(variables ?? {}), [variables]);

  useEffect(() => {
    pageRef.current = 0;
  }, []);

  const getKey = useCallback(
    (opt: TOption): string | number => (getOptionKey ? getOptionKey(opt) : opt.id),
    [getOptionKey]
  );

  useEffect(() => {
    setOptions([]);
    setHasMoreOptions(true);
    setAwaitingFirstPage(true);
    pageRef.current = 0;
  }, [variablesKey, additionalSearchString]);

  const search = useMemo(
    () => `${additionalSearchString} ${localSearch || ""}`.trim(),
    [additionalSearchString, localSearch]
  );
  const [debouncedSearch] = useDebounce(search, 150);

  const variablesWithPagination: VariablesType = useMemo(
    () => ({
      ...variables,
      search,
      page: 0,
      pageSize,
      sort: sortModel,
    }),
    [pageSize, search, sortModel, variables]
  );

  const {
    data,
    fetchMore,
    error: dataLoadingError,
  } = useQuery<TData, VariablesType>(query, {
    variables: variablesWithPagination,
    notifyOnNetworkStatusChange: true,
    fetchPolicy,
    skip: disabled || (lazyLoad && !isOpen),
  });

  // Track latest debounced search to ignore stale fetchMore responses
  const latestDebouncedSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    latestDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  // Populate options only with fresh data for page 0 (avoid previousData bleed on new searches)
  useEffect(() => {
    if (!data) return; // wait for fresh network data for new searches
    const items = getItems(data);
    setOptions(items);
    setHasMoreOptions(items.length >= pageSize);
    onOptionsUpdated?.(items);
    setAwaitingFirstPage(false);
  }, [data, pageSize, getItems, onOptionsUpdated]);

  // Handler for input changes in the autocomplete
  const handleInputChange = useCallback(
    (event: SyntheticEvent<Element, Event>, newInputValue: string) => {
      setLocalSearch(newInputValue);
      setLocalPageSize(pageSize);
      setOptions([]);
      setHasMoreOptions(true);
      setAwaitingFirstPage(true);
      pageRef.current = 0;
    },
    [pageSize]
  );
  // Handler for scrolling in the autocomplete listbox
  const handleScroll = useCallback(
    async (event: React.UIEvent<HTMLUListElement>) => {
      // Add a buffer to trigger loading before reaching the absolute bottom
      const BUFFER_PX = 24;
      const bottom =
        event.currentTarget.scrollHeight <=
        event.currentTarget.scrollTop + event.currentTarget.clientHeight + BUFFER_PX;

      if (bottom && hasMoreOptions && !awaitingFirstPage && !isFetchingMoreRef.current) {
        isFetchingMoreRef.current = true;
        setIsFetchingMore(true);
        const requestSearch = debouncedSearch;
        const nextPage = pageRef.current + 1;
        // Optimistically advance page ref to avoid duplicate same-page requests
        pageRef.current = nextPage;
        try {
          const res = await fetchMore({
            variables: {
              ...variables,
              search: requestSearch,
              page: nextPage,
              pageSize,
              sort: sortModel,
            },
          });
          const fetched = res.data;
          const newItems = fetched ? getItems(fetched) : [];
          // If the search changed while this request was in-flight, drop these results
          if (latestDebouncedSearchRef.current !== requestSearch) {
            return;
          }
          if (newItems.length) {
            setOptions((prev) => {
              const byKey = new Map<string | number, TOption>();
              for (const opt of prev) byKey.set(getKey(opt), opt);
              for (const opt of newItems) byKey.set(getKey(opt), opt);
              const merged = Array.from(byKey.values());
              onOptionsUpdated?.(merged);
              return merged;
            });
            setHasMoreOptions(newItems.length >= pageSize);
          }
        } finally {
          isFetchingMoreRef.current = false;
          setIsFetchingMore(false);
        }
      }
    },
    [
      debouncedSearch,
      fetchMore,
      getItems,
      getKey,
      hasMoreOptions,
      onOptionsUpdated,
      pageSize,
      sortModel,
      variables,
      awaitingFirstPage,
    ]
  );

  const isLoadingOptions = awaitingFirstPage || isFetchingMore;
  const showLoadingSpinner = isOpen && (Boolean(loading) || isLoadingOptions);
  return (
    <Box sx={{ minWidth: 0, width: "100%" }}>
      {dataLoadingError && (
        <Typography variant="body2" color="error" sx={{ mb: 0.5 }}>
          An error occurred while searching.
        </Typography>
      )}
      <Autocomplete
        ref={ref}
        fullWidth={fullWidth}
        multiple={multiple}
        filterSelectedOptions={filterSelectedOptions}
        options={awaitingFirstPage ? [] : options}
        disableClearable={disableClearable}
        getOptionLabel={getOptionLabel}
        // Disable the built-in filtering since it is handled server-side
        filterOptions={filterOptions ?? ((x) => x)}
        onInputChange={handleInputChange}
        isOptionEqualToValue={isOptionEqualToValue ?? ((option, value) => option?.id === value?.id)}
        onChange={(_, value) => {
          onChange?.(value as TMultiple extends true ? TOption[] : TOption | null);
        }}
        value={value}
        disabled={disabled}
        getOptionDisabled={getOptionDisabled}
        renderOption={(props, option) => {
          if (renderOption) return renderOption(props, option);
          return (
            <li {...props} key={String(getKey(option))}>
              {getOptionLabel(option)}
            </li>
          );
        }}
        sx={sx}
        onOpen={() => {
          setIsOpen(true);
          // Only reset when we've paged previously; avoid empty flicker on initial open
          if (pageRef.current > 0) {
            setOptions([]);
            setHasMoreOptions(true);
            pageRef.current = 0;
          }
          onOpenProp?.();
        }}
        onClose={() => {
          setIsOpen(false);
          onCloseProp?.();
        }}
        onBlur={onBlur}
        renderInput={(params) => (
          <TextField
            {...params}
            {...textFieldProps}
            inputProps={{
              ...params.inputProps,
              ...textFieldProps?.inputProps,
              // Always include attributes to discourage password managers and autofill
              "data-lpignore": "true",
              "data-form-type": "other",
              ...(hideLabel ? { "aria-label": placeholderLabel } : {}),
            }}
            error={!!error}
            label={hideLabel ? undefined : placeholderLabel}
            InputProps={{
              ...params.InputProps,
              ...textFieldProps?.InputProps,
              endAdornment: (
                <>
                  {showLoadingSpinner ? <CircularProgress color="inherit" size={20} /> : null}
                  {additionalEndAdornment}
                  {textFieldProps?.InputProps?.endAdornment}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={{
          ...listboxProps,
          onScroll: (e) => {
            listboxProps?.onScroll?.(e);
            handleScroll(e);
          },
        }}
        loading={Boolean(loading) || isLoadingOptions}
        loadingText="Loading..."
        noOptionsText={noOptionsText}
      />
      <ErrorDisplay error={error} helperText={helperText} hideErrors={hideErrors} />
    </Box>
  );
}

const AsyncAutocomplete = forwardRef(AsyncAutocompleteComponent) as <
  TData,
  TOption extends { id: string | number },
  TAdditionalVars extends Record<string, unknown>,
  TMultiple extends boolean | undefined = false,
>(
  props: AsyncAutocompleteProps<TData, TOption, TAdditionalVars, TMultiple> & {
    ref?: React.ForwardedRef<HTMLDivElement>;
  }
) => React.ReactElement;

export default AsyncAutocomplete;
